/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { BUILTIN_TOOLBAR_KEYS, createEditorToolbarActions } from '@atlaskit/native-embeds-common';
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types';

import { NativeEmbedChildCommandType } from './common/types/commands/main';
import LoadingOverlay from './common/ui/loading-overlay';

export const WHITEBOARD_EXPERIENCE_MANIFEST_ID: ExperienceManifestId =
	experienceManifestId('whiteboard');

const isWhiteboardUrl = (url: string): boolean =>
	// @ts-ignore - TS1503 TypeScript 5.9.2 upgrade
	url.match(/\/wiki\/spaces\/?.*\/whiteboard\/(?<resourceId>\d+)(\?\/)?/) ||
	url.match(
		// @ts-ignore - TS1503 TypeScript 5.9.2 upgrade
		/\/wiki\/spaces\/?.*\/whiteboard\/(?<resourceId>[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12})(\?\/)?/,
	);

/**
 * Experience manifest for a whiteboard embed.
 * Register with getNativeEmbedFacade().registerExperience(WhiteboardExperienceManifest)
 * to obtain a Experience for EmbedFrameUI.
 */
export const WhiteboardExperienceManifest = {
	id: WHITEBOARD_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, cmd: NativeEmbedChildCommand): void => {
		switch (cmd.type) {
			case NativeEmbedChildCommandType.FreezeFrameResponse:
				// TODO store the position and size of the frozen frame
				break;
			case NativeEmbedChildCommandType.UnfreezeFrameResponse:
				// TODO remove the position and size of the frozen frame from storage
				break;
		}
	},
	isUrlMatch: isWhiteboardUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	queryParams: { disableCanvasScroll: 'true' },
	isActive: () => {
		return true;
	},
	uiConfig: {
		editorToolbarActions: createEditorToolbarActions({
			items: [
				BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.EMBED,
				BUILTIN_TOOLBAR_KEYS.EDIT,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
				BUILTIN_TOOLBAR_KEYS.BORDER,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
				BUILTIN_TOOLBAR_KEYS.OPEN_IN_NEW_WINDOW,
				BUILTIN_TOOLBAR_KEYS.EDIT_URL,
				BUILTIN_TOOLBAR_KEYS.SEPARATOR,
			],
			moreItems: [
				BUILTIN_TOOLBAR_KEYS.ALWAYS_SHOW_TITLE,
				BUILTIN_TOOLBAR_KEYS.COPY_LINK,
				BUILTIN_TOOLBAR_KEYS.DELETE,
			],
		}),
		renderLoadingComponent: (props: LoadingComponentProps) => (
			<LoadingOverlay animationDisabled={props.animationDisabled} testId={props.testId} />
		),
		titleIcon: WhiteboardIcon,
		appearance: 'overlay',
	},
} as const satisfies ExperienceManifest;
